import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3, Users, Eye, Upload, Sparkles, FileDown } from "lucide-react";

const KPI_ICONS = { source_uploaded: Upload, ai_brief_generated: Sparkles, report_export_created: FileDown };
const KPI_LABELS = { source_uploaded: "Sources uploaded", ai_brief_generated: "AI briefs generated", report_export_created: "Report exports" };

function Kpi({ id, label, value, Icon }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid={`analytics-kpi-${id}`}>
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="w-3.5 h-3.5" />
        <p className="font-mono text-[9px] uppercase tracking-widest">{label}</p>
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1.5">{value}</p>
    </div>
  );
}

function BarList({ title, items, testid }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-3">{title}</p>
      {items.length === 0 && <p className="text-[11px] text-slate-400">No data yet.</p>}
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.name} className="relative rounded-md overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-teal-50" style={{ width: `${(i.count / max) * 100}%` }} />
            <div className="relative flex justify-between px-2 py-1 text-[11px]">
              <span className="text-slate-700 truncate max-w-[75%]">{i.name}</span>
              <span className="font-mono text-slate-500">{i.count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setError(null);
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 403 ? "Admin access required to view analytics." : "Could not load analytics — please retry."));
  }, [days]);
  if (error) return <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="analytics-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-400 p-6" data-testid="analytics-loading">Loading analytics…</p>;
  return (
    <div className="space-y-5" data-testid="analytics-page">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[11px] text-slate-500" data-testid="analytics-privacy-note">
          Privacy-friendly first-party tracking — no cookies, no third parties. Admin pages excluded.
        </p>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`} aria-pressed={days === d}
              className={`text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${days === d ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi id="pageviews" label="Page views" value={data.pageviews} Icon={Eye} />
        <Kpi id="visitors" label="Unique visitors" value={data.visitors} Icon={Users} />
        {Object.entries(data.conversions).map(([k, v]) => (
          <Kpi key={k} id={k} label={KPI_LABELS[k] || k} value={v} Icon={KPI_ICONS[k] || BarChart3} />
        ))}
      </div>
      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="analytics-series-chart">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-3">Daily views vs visitors</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} width={28} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="views" stroke="#0f766e" fill="#ccfbf1" strokeWidth={1.5} name="Views" />
            <Area type="monotone" dataKey="visitors" stroke="#4338ca" fill="#e0e7ff" strokeWidth={1.5} name="Visitors" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BarList title="Top pages" items={data.pages} testid="analytics-list-pages" />
        <BarList title="Traffic sources" items={data.sources} testid="analytics-list-sources" />
        <BarList title="Countries" items={data.countries} testid="analytics-list-countries" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="analytics-device-chart">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-3">Devices</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={data.devices}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 9 }} allowDecimals={false} width={28} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Bar dataKey="count" fill="#0f766e" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <BarList title="Top exported reports" items={data.top_exports} testid="analytics-list-exports" />
      </div>
    </div>
  );
}
